const keySignupAttribution = "signup-attribution";

export type SignupAttribution = {
  gclid: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  provider: string | null;
  referrer: string | null;
  landingPath: string;
  timestamp: string;
};

// gtag stores the Google Ads click ID in the first-party cookie _gcl_aw
// (format "GCL.<timestamp>.<gclid>") on .expensly.ch — set by the marketing
// site's tag, readable here on the app subdomain
const gclidFromCookie = (): string | null => {
  const match = document.cookie.match(/(?:^|;\s*)_gcl_aw=([^;]+)/);
  if (!match) {
    return null;
  }
  const parts = decodeURIComponent(match[1]).split(".");
  return parts.length >= 3 ? parts.slice(2).join(".") : null;
};

// Capture first-touch attribution from URL params at app load.
// Must run before the router/effects strip search params (see onboarding/auth.tsx).
export const captureAttribution = () => {
  try {
    if (localStorage.getItem(keySignupAttribution)) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const attribution: SignupAttribution = {
      gclid: params.get("gclid") ?? gclidFromCookie(),
      utmSource: params.get("utm_source"),
      utmMedium: params.get("utm_medium"),
      utmCampaign: params.get("utm_campaign"),
      provider: params.get("provider"),
      referrer: document.referrer || null,
      landingPath: window.location.pathname,
      timestamp: new Date().toISOString(),
    };

    // Require a real campaign/marketplace signal — referrer alone is any
    // external link and would attach noise to users as "attribution"
    const hasSignal =
      attribution.gclid ||
      attribution.utmSource ||
      attribution.utmMedium ||
      attribution.utmCampaign ||
      attribution.provider;

    if (hasSignal) {
      localStorage.setItem(keySignupAttribution, JSON.stringify(attribution));
    }
  } catch {
    // localStorage unavailable (private mode etc.) — skip silently
  }
};

export const getStoredAttribution = (): string | null => {
  try {
    return localStorage.getItem(keySignupAttribution);
  } catch {
    return null;
  }
};

export const clearStoredAttribution = () => {
  try {
    localStorage.removeItem(keySignupAttribution);
  } catch {
    // ignore
  }
};
